import type { MicrophoneInputState } from './audioAnalyserBus';

export interface ViewerMicrophoneSource {
    enableMic(): Promise<boolean>;
    disableMic(): void;
    getMicState(): MicrophoneInputState;
    subscribeMicState(listener: (state: MicrophoneInputState) => void): () => void;
}

const MIC_STATE_COPY: Record<MicrophoneInputState, { label: string; title: string }> = {
    idle: { label: 'Use mic', title: 'Enable microphone input' },
    requesting: { label: 'Allow…', title: 'Waiting for microphone permission' },
    active: { label: 'Mic on', title: 'Stop microphone input' },
    denied: { label: 'Mic denied', title: 'Microphone access was denied. Click to try again.' },
    unavailable: { label: 'No mic', title: 'Microphone input is unavailable in this browser or device' },
};

/**
 * Bind the viewer's deliberate microphone affordance. Permission is requested only by this
 * button's click handler; general viewer pointer and keyboard activity never enters this path.
 */
export function bindViewerMicrophoneControl(
    button: HTMLButtonElement,
    source: ViewerMicrophoneSource,
): () => void {
    const label = button.querySelector<HTMLElement>('.storysplat-mic-label');
    const render = (state: MicrophoneInputState): void => {
        const copy = MIC_STATE_COPY[state];
        button.dataset.state = state;
        button.classList.toggle('active', state === 'active');
        button.classList.toggle('denied', state === 'denied');
        button.classList.toggle('unavailable', state === 'unavailable');
        button.disabled = state === 'requesting' || state === 'unavailable';
        button.setAttribute('aria-busy', state === 'requesting' ? 'true' : 'false');
        button.setAttribute('aria-pressed', state === 'active' ? 'true' : 'false');
        button.setAttribute('aria-label', copy.title);
        button.title = copy.title;
        if (label) label.textContent = copy.label;
    };

    const handleClick = (): void => {
        const state = source.getMicState();
        if (state === 'active') {
            source.disableMic();
            return;
        }
        if (state === 'idle' || state === 'denied') {
            void source.enableMic();
        }
    };

    const unsubscribe = source.subscribeMicState(render);
    button.addEventListener('click', handleClick);
    return () => {
        button.removeEventListener('click', handleClick);
        unsubscribe();
    };
}
